import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import {
  CoberturaDeTipo,
  DiaExpediente,
  PausaAutomatica,
  PausaAutomaticaEscopo,
  TipoDePausa,
  createPausaAutomatica,
  createTipoDePausa,
  deletePausaAutomatica,
  deleteTipoDePausa,
  getCobertura,
  getExpediente,
  getPausasAutomaticas,
  getTiposDePausa,
  horaParaMinutos,
  minutosParaHora,
  setExpediente as salvarExpediente,
  updatePausaAutomatica,
  updateTipoDePausa,
} from '../../../api/pausasAutomaticasApi';
import { fetchCollaborators } from '../../../api/collaboratorsApi';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import CollaboratorModal, {
  ColaboradorSelecionado,
} from '../../../components/modal/collaborator/CollaboratorModal';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de cabeçalho compartilhado (`recepcao-header-row`, `recepcao-title`). O import é
// explícito de propósito: contar com o CSS que a página hospedeira carrega é acoplamento
// invisível — a aba quebraria em silêncio se fosse montada em outra tela (lição do BUG-V74).
import '../../recepcao/pages/Recepcao.css';

/** 0 = domingo, mesma convenção do backend. */
const DIAS = [
  { valor: 1, abreviado: 'Seg', nome: 'Segunda' },
  { valor: 2, abreviado: 'Ter', nome: 'Terça' },
  { valor: 3, abreviado: 'Qua', nome: 'Quarta' },
  { valor: 4, abreviado: 'Qui', nome: 'Quinta' },
  { valor: 5, abreviado: 'Sex', nome: 'Sexta' },
  { valor: 6, abreviado: 'Sáb', nome: 'Sábado' },
  { valor: 0, abreviado: 'Dom', nome: 'Domingo' },
];

const DIAS_UTEIS = [1, 2, 3, 4, 5];

/** Espelha a validação do backend — janela mais longa que isto é erro de digitação provável. */
const DURACAO_MAXIMA_MINUTOS = 240;


/**
 * Passo dos horários no cadastro, em minutos (pedido do Victor, 05/08/2026): a operação pensa em
 * blocos de 15 minutos e não quer detalhar minuto a minuto.
 *
 * A escolha é `<select>` com as opções prontas, e não `<input type="time" step>`: o `step` do input
 * nativo só muda o salto das setas e a validação — o navegador continua aceitando qualquer minuto
 * digitado e **não** exibe as opções. Select é o único jeito de a grade de 15 minutos ser visível e
 * garantida.
 *
 * É conveniência de cadastro, **não** regra de negócio: o backend segue aceitando qualquer minuto,
 * para não recusar configuração já salva (ver `horaForaDaGrade` abaixo).
 */
const PASSO_HORA_MINUTOS = 15;

/** "00:00", "00:15", … "23:45". */
const OPCOES_DE_HORA: string[] = (() => {
  const opcoes: string[] = [];
  for (let minutos = 0; minutos < 24 * 60; minutos += PASSO_HORA_MINUTOS) {
    opcoes.push(minutosParaHora(minutos));
  }
  return opcoes;
})();

interface SelectHoraProps {
  /** "HH:MM" ou vazio. */
  value: string;
  onChange: (valor: string) => void;
  disabled?: boolean;
}

/**
 * Seletor de horário na grade de 15 minutos.
 *
 * Horário **fora da grade** (janela cadastrada antes desta mudança, ou gravada via API) entra como
 * opção extra em vez de ser descartado: sem isso o select exibiria um horário diferente do que está
 * no banco e o próximo "salvar" alteraria a configuração sem ninguém pedir.
 */
const SelectHora: React.FC<SelectHoraProps> = ({ value, onChange, disabled }) => {
  const horaForaDaGrade = !!value && !OPCOES_DE_HORA.includes(value);

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="admin-input"
      disabled={disabled}
    >
      {!value && <option value="">--:--</option>}
      {horaForaDaGrade && <option value={value}>{value}</option>}
      {OPCOES_DE_HORA.map((hora) => (
        <option key={hora} value={hora}>
          {hora}
        </option>
      ))}
    </select>
  );
};

/**
 * Linha da tabela de expediente na tela. O `configurado` NÃO existe no backend: lá "não restringe"
 * é a ausência do dia. A tela precisa das sete linhas de qualquer forma, então guarda o flag e
 * omite os desmarcados no PUT declarativo.
 */
type LinhaExpediente = DiaExpediente & { configurado: boolean };

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que são as pausas automáticas',
    conteudo: (
      <p>
        Janelas de pausa automática são períodos fixos e previsíveis — como o almoço e o lanche —
        descontados do <strong>tempo trabalhado</strong>, sem que o colaborador precise pausar a
        atividade. Quem trabalha das 09:00 às 17:00 com uma janela de almoço de 12:00 às 13:00 tem 7
        horas de tempo trabalhado, não 8.
      </p>
    ),
  },
  {
    titulo: 'Tipos de pausa e janelas',
    conteudo: (
      <>
        <p>
          O <strong>tipo</strong> (Almoço, Lanche, o que a operação precisar) agrupa janelas que
          cumprem a mesma função. Como nem todo colaborador para no mesmo horário, um tipo pode ter
          várias janelas: <strong>Almoço 1</strong> das 11:30 às 12:30 para um time e{' '}
          <strong>Almoço 2</strong> das 12:30 às 13:30 para outro.
        </p>
        <p>
          Um colaborador não pode estar em <strong>duas janelas do mesmo tipo no mesmo dia</strong> —
          o sistema recusa o cadastro, porque isso descontaria dois almoços dele no mesmo dia.
          Janelas de tipos diferentes (um almoço e um lanche) convivem sem problema.
        </p>
        <p>
          Excluir um tipo <strong>não exclui as janelas</strong>: elas continuam descontando, apenas
          ficam sem classificação — e, sem tipo, saem da verificação de duplicidade acima.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quem a janela alcança',
    conteudo: (
      <>
        <p>
          Cada janela vale para <strong>todos os colaboradores</strong> ou apenas para{' '}
          <strong>colaboradores específicos</strong>, escolhidos em <strong>Selecionar
          colaboradores</strong> — a mesma busca por nome usada para colocar colaboradores num
          veículo. Colaborador que não está
          em nenhuma janela de um tipo simplesmente <strong>não tem aquele desconto</strong> — o
          aviso no topo desta tela aponta quem está nessa situação, para o esquecimento não passar
          despercebido e inflar as horas.
        </p>
        <p>
          Colaborador contratado depois entra automaticamente nas janelas de escopo{' '}
          <strong>Todos os colaboradores</strong>, mas precisa ser adicionado à mão nas janelas de
          colaboradores específicos.
        </p>
      </>
    ),
  },
  {
    titulo: 'Horário de funcionamento',
    conteudo: (
      <>
        <p>
          O <strong>horário de funcionamento</strong> define quando a oficina está aberta em cada dia
          da semana. Todo tempo fora dele — antes de abrir, depois de fechar e o dia inteiro nos dias
          fechados — é <strong>descontado do tempo trabalhado</strong>, do mesmo jeito que as janelas
          de pausa.
        </p>
        <p>
          É isso que evita a distorção da atividade que ninguém finalizou: se o colaborador esquece
          de encerrar na sexta e a atividade só é fechada na segunda, o fim de semana inteiro não
          entra como tempo trabalhado.
        </p>
        <p>
          <strong>Dia sem configuração não restringe nada.</strong> Enquanto um dia não é
          configurado, nada é descontado nele e a tela do colaborador não bloqueia nada — foi assim
          que este recurso entrou no ar, sem alterar contas de ninguém.
        </p>
      </>
    ),
  },
  {
    titulo: 'Quando o desconto acontece',
    conteudo: (
      <>
        <p>
          O desconto é calculado <strong>no momento em que a atividade é finalizada</strong>: o
          sistema verifica quais janelas e quais períodos fora do expediente aquele trabalho cruzou e
          registra uma pausa para cada um. Essas pausas aparecem no detalhe do atendimento
          identificadas como <strong>Automática</strong>, junto das que o colaborador registrou.
        </p>
        <p>
          Quem já pausou a atividade por conta própria durante a janela{' '}
          <strong>não é descontado duas vezes</strong> — só o tempo que ficou de fora da pausa manual
          é considerado. Vale o mesmo entre uma janela e o horário de funcionamento: o mesmo minuto
          nunca é descontado duas vezes.
        </p>
        <p>
          O desconto também vale para atividades encerradas pelo sistema (entrega do veículo, remoção
          da liberação, saneamento de atividades esquecidas em aberto). Nesses casos, se o período
          abranger vários dias, cada dia é calculado com a sua própria configuração.
        </p>
      </>
    ),
  },
  {
    titulo: 'Durante a janela e fora do expediente',
    conteudo: (
      <p>
        Enquanto uma janela está em vigor — ou enquanto a oficina está fora do horário de
        funcionamento — a tela de atividades do colaborador <strong>bloqueia as ações</strong> de
        iniciar, pausar, retomar e finalizar, com um aviso explicando o motivo. O bloqueio liga e
        desliga sozinho no horário configurado, sem recarregar a tela, e alcança apenas os
        colaboradores da janela em questão. As demais telas do sistema continuam livres para ele.
      </p>
    ),
  },
  {
    titulo: 'Efeito das alterações',
    conteudo: (
      <p>
        Criar, alterar, desativar ou excluir uma janela — ou mudar o horário de funcionamento — vale
        apenas para as atividades <strong>finalizadas dali em diante</strong>. Horas já registradas e
        aprovadas nunca são recalculadas, por isso confira a configuração antes de salvar. Descontos
        já aplicados continuam no histórico mesmo que a janela seja excluída depois.
      </p>
    ),
  },
  {
    titulo: 'Trabalho durante a janela',
    conteudo: (
      <p>
        O desconto é aplicado sempre que a atividade cruza a janela, inclusive quando o colaborador
        trabalhou durante o horário do almoço. Não existe hoje uma forma de registrar essa exceção —
        se isso for comum na sua operação, avalie ajustar os horários ou os colaboradores da janela.
        Feriados também não são tratados: o sistema segue o dia da semana.
      </p>
    ),
  },
];

const PausasAutomaticasSection: React.FC = () => {
  const [janelas, setJanelas] = useState<PausaAutomatica[]>([]);
  const [tipos, setTipos] = useState<TipoDePausa[]>([]);
  const [expediente, setExpedienteState] = useState<DiaExpediente[]>([]);
  const [cobertura, setCobertura] = useState<CoberturaDeTipo[]>([]);
  const [carregando, setCarregando] = useState(true);

  // Seleção de colaboradores pelo modal paginado compartilhado (o mesmo das telas de veículo).
  const [colaboradoresPagina, setColaboradoresPagina] = useState<any[]>([]);
  const [totalColaboradores, setTotalColaboradores] = useState(0);
  const [isColaboradoresModalOpen, setIsColaboradoresModalOpen] = useState(false);
  /** Rascunho do modal: só entra no formulário quando o usuário confirma. */
  const [selecaoNoModal, setSelecaoNoModal] = useState<ColaboradorSelecionado[]>([]);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [paraExcluir, setParaExcluir] = useState<PausaAutomatica | null>(null);
  const [tipoParaExcluir, setTipoParaExcluir] = useState<TipoDePausa | null>(null);

  // Formulário da janela — serve para criar e para editar (quando `editandoId` está preenchido).
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [tipoId, setTipoId] = useState<string>('');
  const [diasSelecionados, setDiasSelecionados] = useState<number[]>(DIAS_UTEIS);
  const [horaInicio, setHoraInicio] = useState('12:00');
  const [horaFim, setHoraFim] = useState('13:00');
  const [escopo, setEscopo] = useState<PausaAutomaticaEscopo>('TODOS');
  /** Guarda o nome junto com o id: o resumo na tela não depende de ter a lista inteira carregada. */
  const [colaboradoresDaJanela, setColaboradoresDaJanela] = useState<ColaboradorSelecionado[]>([]);
  const [ativa, setAtiva] = useState(true);

  // Formulário de tipo (criar/renomear).
  const [nomeTipo, setNomeTipo] = useState('');
  const [tipoEditandoId, setTipoEditandoId] = useState<string | null>(null);

  // Rascunho do expediente — só vai ao servidor quando o admin salva.
  const [rascunhoExpediente, setRascunhoExpediente] = useState<LinhaExpediente[]>([]);
  const [salvandoExpediente, setSalvandoExpediente] = useState(false);

  /**
   * Carrega uma página de colaboradores para o modal.
   *
   * `useCallback` sem dependências é exigência do `CollaboratorModal`: ele dispara a carga num
   * efeito que observa a identidade desta função — um callback recriado a cada render levaria o
   * carregamento a disparar em loop (está documentado no próprio modal).
   *
   * Sem `limit`: o default do `fetchCollaborators` é 5, o mesmo `pageSize` que o modal usa na busca.
   */
  const carregarPaginaDeColaboradores = useCallback(async (pagina: number) => {
    try {
      const { users, total } = await fetchCollaborators(pagina);
      setColaboradoresPagina(users);
      setTotalColaboradores(total);
    } catch {
      toast.error('Erro ao carregar os colaboradores.');
    }
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [janelasApi, tiposApi, expedienteApi, coberturaApi] = await Promise.all([
        getPausasAutomaticas(),
        getTiposDePausa(),
        getExpediente(),
        getCobertura(),
      ]);

      setJanelas(janelasApi);
      setTipos(tiposApi);
      setExpedienteState(expedienteApi);
      setRascunhoExpediente(montarRascunho(expedienteApi));
      setCobertura(coberturaApi);
    } catch {
      toast.error('Erro ao carregar a configuração de pausas automáticas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Primeira página adiantada: sem isso o modal abre exibindo "Nenhum colaborador encontrado" no
  // intervalo até a busca responder.
  useEffect(() => {
    void carregarPaginaDeColaboradores(1);
  }, [carregarPaginaDeColaboradores]);

  const limparFormulario = () => {
    setEditandoId(null);
    setNome('');
    setTipoId('');
    setDiasSelecionados(DIAS_UTEIS);
    setHoraInicio('12:00');
    setHoraFim('13:00');
    setEscopo('TODOS');
    setColaboradoresDaJanela([]);
    setAtiva(true);
  };

  const iniciarEdicao = (janela: PausaAutomatica) => {
    setEditandoId(janela.id);
    setNome(janela.nome);
    setTipoId(janela.tipoId ?? '');
    setDiasSelecionados([...janela.diasSemana]);
    setHoraInicio(minutosParaHora(janela.horaInicioMinutos));
    setHoraFim(minutosParaHora(janela.horaFimMinutos));
    setEscopo(janela.escopo);
    setColaboradoresDaJanela(
      janela.colaboradores.map((colaborador) => ({ id: colaborador.id, fullName: colaborador.nome })),
    );
    setAtiva(janela.ativa);
  };

  const abrirModalDeColaboradores = () => {
    // O modal edita um rascunho: fechar no Cancelar não pode mexer no formulário.
    setSelecaoNoModal(colaboradoresDaJanela);
    setIsColaboradoresModalOpen(true);
  };

  /** Espelha as validações do backend para o usuário não descobrir o erro só no submit. */
  const erroDeValidacao = useMemo(() => {
    if (!nome.trim() || nome.trim().length < 2) return 'Informe o nome da janela (mínimo 2 caracteres).';
    if (!diasSelecionados.length) return 'Selecione ao menos um dia da semana.';

    const inicio = horaParaMinutos(horaInicio);
    const fim = horaParaMinutos(horaFim);
    if (inicio === null || fim === null) return 'Informe os horários de início e término.';
    if (fim <= inicio) return 'O horário de término deve ser posterior ao de início.';
    if (fim - inicio > DURACAO_MAXIMA_MINUTOS) {
      return `A janela não pode passar de ${DURACAO_MAXIMA_MINUTOS / 60} horas. Verifique os horários.`;
    }
    if (escopo === 'ESPECIFICOS' && !colaboradoresDaJanela.length) {
      return 'Selecione os colaboradores da janela, ou mude o escopo para "Todos os colaboradores".';
    }
    return null;
  }, [nome, diasSelecionados, horaInicio, horaFim, escopo, colaboradoresDaJanela]);

  const handleSalvar = async () => {
    if (erroDeValidacao) {
      toast.warn(erroDeValidacao);
      return;
    }

    const payload = {
      nome: nome.trim(),
      tipoId: tipoId || null,
      diasSemana: diasSelecionados,
      horaInicioMinutos: horaParaMinutos(horaInicio) as number,
      horaFimMinutos: horaParaMinutos(horaFim) as number,
      escopo,
      // O backend recusa seleção com escopo TODOS — mandar vazio mantém as duas pontas coerentes.
      colaboradorIds:
        escopo === 'ESPECIFICOS' ? colaboradoresDaJanela.map((colaborador) => colaborador.id) : [],
      ativa,
    };

    setSalvando(true);
    try {
      if (editandoId) {
        await updatePausaAutomatica(editandoId, payload);
        toast.success('Janela atualizada. O novo horário vale para as próximas atividades finalizadas.');
      } else {
        await createPausaAutomatica(payload);
        toast.success('Janela criada. O desconto vale para as próximas atividades finalizadas.');
      }
      limparFormulario();
      await carregar();
    } catch (erro: any) {
      const mensagem =
        erro?.response?.data?.message ?? 'Não foi possível salvar a janela de pausa automática.';
      toast.error(Array.isArray(mensagem) ? mensagem.join(' ') : mensagem);
    } finally {
      setSalvando(false);
    }
  };

  const handleAlternarAtiva = async (janela: PausaAutomatica) => {
    try {
      await updatePausaAutomatica(janela.id, { ativa: !janela.ativa });
      toast.success(
        janela.ativa
          ? 'Janela desativada. As próximas atividades finalizadas não terão esse desconto.'
          : 'Janela ativada. O desconto vale para as próximas atividades finalizadas.',
      );
      await carregar();
    } catch (erro: any) {
      toast.error(erro?.response?.data?.message ?? 'Não foi possível alterar a janela.');
    }
  };

  const handleExcluir = async () => {
    if (!paraExcluir) return;
    try {
      await deletePausaAutomatica(paraExcluir.id);
      toast.success('Janela excluída.');
      if (editandoId === paraExcluir.id) limparFormulario();
      await carregar();
    } catch (erro: any) {
      toast.error(erro?.response?.data?.message ?? 'Não foi possível excluir a janela.');
    } finally {
      setParaExcluir(null);
    }
  };

  // --- Tipos ---

  const handleSalvarTipo = async () => {
    const valor = nomeTipo.trim();
    if (valor.length < 2) {
      toast.warn('Informe o nome do tipo (mínimo 2 caracteres).');
      return;
    }

    try {
      if (tipoEditandoId) {
        await updateTipoDePausa(tipoEditandoId, valor);
        toast.success('Tipo renomeado.');
      } else {
        await createTipoDePausa(valor);
        toast.success('Tipo criado.');
      }
      setNomeTipo('');
      setTipoEditandoId(null);
      await carregar();
    } catch (erro: any) {
      toast.error(erro?.response?.data?.message ?? 'Não foi possível salvar o tipo de pausa.');
    }
  };

  const handleExcluirTipo = async () => {
    if (!tipoParaExcluir) return;
    try {
      await deleteTipoDePausa(tipoParaExcluir.id);
      toast.success('Tipo excluído. As janelas dele continuam ativas, sem classificação.');
      if (tipoEditandoId === tipoParaExcluir.id) {
        setTipoEditandoId(null);
        setNomeTipo('');
      }
      await carregar();
    } catch (erro: any) {
      toast.error(erro?.response?.data?.message ?? 'Não foi possível excluir o tipo.');
    } finally {
      setTipoParaExcluir(null);
    }
  };

  // --- Expediente ---

  const atualizarDiaDoRascunho = (diaSemana: number, mudanca: Partial<LinhaExpediente>) => {
    setRascunhoExpediente((anterior) =>
      anterior.map((dia) => (dia.diaSemana === diaSemana ? { ...dia, ...mudanca } : dia)),
    );
  };

  const erroDoExpediente = useMemo(() => {
    for (const dia of rascunhoExpediente) {
      if (!dia.configurado || !dia.aberto) continue;
      if (dia.aberturaMinutos === null || dia.fechamentoMinutos === null) {
        return `Informe abertura e fechamento de ${nomeDoDia(dia.diaSemana)}, ou marque como fechado.`;
      }
      if (dia.fechamentoMinutos <= dia.aberturaMinutos) {
        return `Em ${nomeDoDia(dia.diaSemana)}, o fechamento deve ser posterior à abertura.`;
      }
    }
    return null;
  }, [rascunhoExpediente]);

  const handleSalvarExpediente = async () => {
    if (erroDoExpediente) {
      toast.warn(erroDoExpediente);
      return;
    }

    setSalvandoExpediente(true);
    try {
      // Só os dias marcados como configurados vão para o servidor: dia de fora deixa de restringir.
      const atualizado = await salvarExpediente(
        rascunhoExpediente.filter((dia) => dia.configurado) as DiaExpediente[],
      );
      setExpedienteState(atualizado);
      setRascunhoExpediente(montarRascunho(atualizado));
      toast.success(
        'Horário de funcionamento salvo. Vale para as próximas atividades finalizadas — as horas já registradas não mudam.',
      );
    } catch (erro: any) {
      const mensagem = erro?.response?.data?.message ?? 'Não foi possível salvar o horário de funcionamento.';
      toast.error(Array.isArray(mensagem) ? mensagem.join(' ') : mensagem);
    } finally {
      setSalvandoExpediente(false);
    }
  };

  const expedienteAlterado = useMemo(
    () => JSON.stringify(montarRascunho(expediente)) !== JSON.stringify(rascunhoExpediente),
    [expediente, rascunhoExpediente],
  );

  const rotuloDias = (dias: number[]) => {
    const ordenados = DIAS.filter((d) => dias.includes(d.valor));
    if (ordenados.length === 7) return 'Todos os dias';
    if (dias.length === 5 && DIAS_UTEIS.every((d) => dias.includes(d))) return 'Seg a Sex';
    return ordenados.map((d) => d.abreviado).join(', ');
  };

  const rotuloEscopo = (janela: PausaAutomatica) => {
    if (janela.escopo === 'TODOS') return 'Todos os colaboradores';
    if (!janela.colaboradores.length) return 'Nenhum colaborador';
    return janela.colaboradores.map((colaborador) => colaborador.nome).join(', ');
  };

  const tiposComCoberturaIncompleta = cobertura.filter((item) => item.naoCobertos.length > 0);

  return (
    <>
      <div className="admin-aba-cabecalho">
        <div className="admin-card-title">
          Pausas Automáticas
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      {/* Aviso de cobertura: quem está fora de toda janela de um tipo não tem o desconto, e isso
          passaria despercebido até alguém estranhar as horas na aprovação. */}
      {!carregando && tiposComCoberturaIncompleta.length > 0 && (
        <div className="admin-card" style={{ borderLeft: '4px solid #d97706' }}>
          <div className="admin-card-title">Colaboradores sem pausa configurada</div>
          {tiposComCoberturaIncompleta.map((item) => (
            <p key={item.tipo.id} style={{ fontSize: '13px', margin: '4px 0' }}>
              <strong>{item.tipo.nome}</strong>: {item.naoCobertos.length} de{' '}
              {item.totalColaboradores} colaboradores não estão em nenhuma janela ativa —{' '}
              {item.naoCobertos.map((colaborador) => colaborador.nome).join(', ')}.
            </p>
          ))}
          <p style={{ fontSize: '12px', color: 'var(--tema-texto-3, #6b7280)', marginBottom: 0 }}>
            Sem estar em uma janela, o tempo dessa pausa não é descontado do tempo trabalhado dessas
            pessoas.
          </p>
        </div>
      )}

      {/* --- Tipos de pausa --- */}
      <div className="admin-card">
        <div className="admin-card-title">Tipos de Pausa</div>
        <p style={{ fontSize: '12px', color: 'var(--tema-texto-3, #6b7280)', marginTop: 0 }}>
          O tipo agrupa janelas com a mesma função (Almoço, Lanche). Um colaborador não pode estar em
          duas janelas do mesmo tipo no mesmo dia.
        </p>

        <div className="admin-form-grid">
          <div className="admin-input-group">
            <label className="admin-label">{tipoEditandoId ? 'Renomear tipo' : 'Novo tipo'}</label>
            <input
              type="text"
              value={nomeTipo}
              onChange={(e) => setNomeTipo(e.target.value)}
              placeholder="Ex: Almoço"
              maxLength={60}
              className="admin-input"
            />
          </div>
        </div>

        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => void handleSalvarTipo()} className="admin-btn admin-btn-primary">
            {tipoEditandoId ? 'Salvar nome' : 'Criar tipo'}
          </button>
          {tipoEditandoId && (
            <button
              onClick={() => {
                setTipoEditandoId(null);
                setNomeTipo('');
              }}
              className="admin-btn admin-btn-secondary"
            >
              Cancelar
            </button>
          )}
        </div>

        {tipos.length > 0 && (
          <div style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {tipos.map((tipo) => (
              <span
                key={tipo.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  background: 'var(--tema-fundo, #f3f4f6)',
                  fontSize: '13px',
                }}
              >
                {tipo.nome}
                <span style={{ color: 'var(--tema-texto-3, #6b7280)', fontSize: '12px' }}>
                  ({tipo.totalJanelas ?? 0} {tipo.totalJanelas === 1 ? 'janela' : 'janelas'})
                </span>
                <button
                  onClick={() => {
                    setTipoEditandoId(tipo.id);
                    setNomeTipo(tipo.nome);
                  }}
                  className="admin-btn admin-btn-secondary"
                  style={{ padding: '2px 6px', fontSize: '11px' }}
                >
                  Renomear
                </button>
                <button
                  onClick={() => setTipoParaExcluir(tipo)}
                  className="admin-btn admin-btn-danger"
                  style={{ padding: '2px 6px', fontSize: '11px' }}
                >
                  Excluir
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* --- Horário de funcionamento --- */}
      <div className="admin-card">
        <div className="admin-card-title">Horário de Funcionamento</div>
        <p style={{ fontSize: '12px', color: 'var(--tema-texto-3, #6b7280)', marginTop: 0 }}>
          O tempo fora do horário de funcionamento é descontado do tempo trabalhado, do mesmo jeito
          que as janelas de pausa. <strong>Dia não configurado não restringe nada</strong> — nem
          desconto, nem bloqueio na tela do colaborador.
        </p>

        <div style={{ overflowX: 'auto' }}>
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--tema-linha, #e5e7eb)' }}>
                <th style={{ padding: '8px' }}>Dia</th>
                <th style={{ padding: '8px' }}>Configurado</th>
                <th style={{ padding: '8px' }}>Situação</th>
                <th style={{ padding: '8px' }}>Abre</th>
                <th style={{ padding: '8px' }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {rascunhoExpediente.map((dia) => (
                <tr key={dia.diaSemana} style={{ borderBottom: '1px solid var(--tema-linha, #f3f4f6)' }}>
                  <td style={{ padding: '8px' }}>{nomeDoDia(dia.diaSemana)}</td>
                  <td style={{ padding: '8px' }}>
                    <label className="admin-checkbox-label">
                      <input
                        type="checkbox"
                        checked={!!dia.configurado}
                        onChange={(e) =>
                          atualizarDiaDoRascunho(dia.diaSemana, { configurado: e.target.checked })
                        }
                      />
                    </label>
                  </td>
                  <td style={{ padding: '8px' }}>
                    <select
                      value={dia.aberto ? 'ABERTO' : 'FECHADO'}
                      onChange={(e) =>
                        atualizarDiaDoRascunho(dia.diaSemana, { aberto: e.target.value === 'ABERTO' })
                      }
                      className="admin-input"
                      disabled={!dia.configurado}
                    >
                      <option value="ABERTO">Aberto</option>
                      <option value="FECHADO">Fechado</option>
                    </select>
                  </td>
                  <td style={{ padding: '8px' }}>
                    <SelectHora
                      value={dia.aberturaMinutos !== null ? minutosParaHora(dia.aberturaMinutos) : ''}
                      onChange={(valor) =>
                        atualizarDiaDoRascunho(dia.diaSemana, {
                          aberturaMinutos: horaParaMinutos(valor),
                        })
                      }
                      disabled={!dia.configurado || !dia.aberto}
                    />
                  </td>
                  <td style={{ padding: '8px' }}>
                    <SelectHora
                      value={dia.fechamentoMinutos !== null ? minutosParaHora(dia.fechamentoMinutos) : ''}
                      onChange={(valor) =>
                        atualizarDiaDoRascunho(dia.diaSemana, {
                          fechamentoMinutos: horaParaMinutos(valor),
                        })
                      }
                      disabled={!dia.configurado || !dia.aberto}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {erroDoExpediente && (
          <p style={{ marginTop: '12px', marginBottom: 0, fontSize: '12px', color: 'var(--tema-texto-vermelho, #b91c1c)' }}>
            {erroDoExpediente}
          </p>
        )}

        <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => void handleSalvarExpediente()}
            disabled={salvandoExpediente || !!erroDoExpediente || !expedienteAlterado}
            className="admin-btn admin-btn-primary"
          >
            {salvandoExpediente ? 'Salvando...' : 'Salvar horário de funcionamento'}
          </button>
          {expedienteAlterado && (
            <button
              onClick={() => setRascunhoExpediente(montarRascunho(expediente))}
              className="admin-btn admin-btn-secondary"
            >
              Descartar alterações
            </button>
          )}
        </div>
      </div>

      {/* --- Janela --- */}
      <div className="admin-card">
        <div className="admin-card-title">
          {editandoId ? 'Editar Janela de Pausa' : 'Criar Janela de Pausa'}
        </div>

        <div className="admin-form-grid">
          <div className="admin-input-group">
            <label className="admin-label">Nome</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Almoço 1"
              maxLength={100}
              className="admin-input"
            />
          </div>

          <div className="admin-input-group">
            <label className="admin-label">Tipo</label>
            <select
              value={tipoId}
              onChange={(e) => setTipoId(e.target.value)}
              className="admin-input"
            >
              <option value="">Sem tipo</option>
              {tipos.map((tipo) => (
                <option key={tipo.id} value={tipo.id}>
                  {tipo.nome}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-input-group fixed-width">
            <label className="admin-label">Início</label>
            <SelectHora value={horaInicio} onChange={setHoraInicio} />
          </div>

          <div className="admin-input-group fixed-width">
            <label className="admin-label">Término</label>
            <SelectHora value={horaFim} onChange={setHoraFim} />
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label className="admin-label" style={{ marginBottom: '8px', display: 'block' }}>
            Dias da semana
          </label>
          <div className="admin-checkbox-group">
            {DIAS.map((dia) => (
              <label key={dia.valor} className="admin-checkbox-label">
                <input
                  type="checkbox"
                  checked={diasSelecionados.includes(dia.valor)}
                  onChange={(e) =>
                    setDiasSelecionados((prev) =>
                      e.target.checked
                        ? [...prev, dia.valor]
                        : prev.filter((valor) => valor !== dia.valor),
                    )
                  }
                />
                {dia.abreviado}
              </label>
            ))}
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label className="admin-label" style={{ marginBottom: '8px', display: 'block' }}>
            Quem a janela alcança
          </label>
          <div className="admin-checkbox-group">
            <label className="admin-checkbox-label">
              <input
                type="radio"
                name="escopo-pausa"
                checked={escopo === 'TODOS'}
                onChange={() => {
                  setEscopo('TODOS');
                  setColaboradoresDaJanela([]);
                }}
              />
              Todos os colaboradores
            </label>
            <label className="admin-checkbox-label">
              <input
                type="radio"
                name="escopo-pausa"
                checked={escopo === 'ESPECIFICOS'}
                onChange={() => setEscopo('ESPECIFICOS')}
              />
              Colaboradores específicos
            </label>
          </div>
        </div>

        {/* Seleção pelo modal paginado com busca (o mesmo das telas de veículo) — a lista de
            colaboradores cresce, e uma lista inteira de checkboxes aqui não se sustenta. */}
        {escopo === 'ESPECIFICOS' && (
          <div style={{ marginTop: '12px' }}>
            <label className="admin-label" style={{ marginBottom: '8px', display: 'block' }}>
              Colaboradores da janela
              {colaboradoresDaJanela.length > 0 ? ` (${colaboradoresDaJanela.length})` : ''}
            </label>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <button
                type="button"
                onClick={abrirModalDeColaboradores}
                className="admin-btn admin-btn-secondary"
              >
                {colaboradoresDaJanela.length ? 'Alterar colaboradores' : 'Selecionar colaboradores'}
              </button>

              {colaboradoresDaJanela.length === 0 && (
                <span style={{ fontSize: '12px', color: 'var(--tema-texto-3, #6b7280)' }}>
                  Nenhum colaborador selecionado.
                </span>
              )}
            </div>

            {colaboradoresDaJanela.length > 0 && (
              <div style={{ marginTop: '8px', fontSize: '13px' }}>
                {colaboradoresDaJanela
                  .map((colaborador) => colaborador.fullName ?? 'Colaborador')
                  .join(', ')}
              </div>
            )}
          </div>
        )}

        <div style={{ marginTop: '16px' }}>
          <label className="admin-checkbox-label">
            <input type="checkbox" checked={ativa} onChange={(e) => setAtiva(e.target.checked)} />
            Janela ativa (desconto aplicado nas próximas atividades finalizadas)
          </label>
        </div>

        {erroDeValidacao && (
          <p style={{ marginTop: '12px', marginBottom: 0, fontSize: '12px', color: 'var(--tema-texto-vermelho, #b91c1c)' }}>
            {erroDeValidacao}
          </p>
        )}

        <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={() => void handleSalvar()}
            disabled={salvando || !!erroDeValidacao}
            className="admin-btn admin-btn-primary"
          >
            {salvando ? 'Salvando...' : editandoId ? 'Salvar alterações' : 'Criar janela'}
          </button>
          {editandoId && (
            <button onClick={limparFormulario} className="admin-btn admin-btn-secondary">
              Cancelar edição
            </button>
          )}
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-card-title">Janelas Cadastradas</div>

        {carregando && <p style={{ fontSize: '13px', color: 'var(--tema-texto-3, #6b7280)' }}>Carregando...</p>}

        {!carregando && janelas.length === 0 && (
          <p style={{ fontSize: '13px', color: 'var(--tema-texto-3, #6b7280)', margin: 0 }}>
            Nenhuma janela cadastrada. Enquanto não houver janela ativa, nenhum desconto de pausa é
            aplicado e o tempo trabalhado continua sendo o período entre início e fim da atividade,
            menos as pausas registradas pelo colaborador e o tempo fora do horário de funcionamento.
          </p>
        )}

        {!carregando && janelas.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--tema-linha, #e5e7eb)' }}>
                  <th style={{ padding: '8px' }}>Nome</th>
                  <th style={{ padding: '8px' }}>Tipo</th>
                  <th style={{ padding: '8px' }}>Dias</th>
                  <th style={{ padding: '8px' }}>Horário</th>
                  <th style={{ padding: '8px' }}>Duração</th>
                  <th style={{ padding: '8px' }}>Alcança</th>
                  <th style={{ padding: '8px' }}>Estado</th>
                  <th style={{ padding: '8px' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {janelas.map((janela) => {
                  const duracao = janela.horaFimMinutos - janela.horaInicioMinutos;
                  return (
                    <tr key={janela.id} style={{ borderBottom: '1px solid var(--tema-linha, #f3f4f6)' }}>
                      <td style={{ padding: '8px' }}>{janela.nome}</td>
                      <td style={{ padding: '8px' }}>
                        {janela.tipo?.nome ?? <span style={{ color: 'var(--tema-texto-3, #6b7280)' }}>Sem tipo</span>}
                      </td>
                      <td style={{ padding: '8px' }}>{rotuloDias(janela.diasSemana)}</td>
                      <td style={{ padding: '8px' }}>
                        {janela.horaInicio} às {janela.horaFim}
                      </td>
                      <td style={{ padding: '8px' }}>
                        {duracao >= 60 ? `${Math.floor(duracao / 60)}h ` : ''}
                        {duracao % 60 > 0 ? `${duracao % 60}min` : ''}
                      </td>
                      <td style={{ padding: '8px', maxWidth: '220px' }}>{rotuloEscopo(janela)}</td>
                      <td style={{ padding: '8px' }}>
                        {janela.ativa ? 'Ativa' : <span className="admin-badge-inactive">Inativa</span>}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => iniciarEdicao(janela)}
                            className="admin-btn admin-btn-secondary"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => void handleAlternarAtiva(janela)}
                            className={`admin-btn ${
                              janela.ativa ? 'admin-btn-danger-outline' : 'admin-btn-success-outline'
                            }`}
                          >
                            {janela.ativa ? 'Desativar' : 'Ativar'}
                          </button>
                          <button
                            onClick={() => setParaExcluir(janela)}
                            className="admin-btn admin-btn-danger"
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mesmo modal das telas de veículo (paginado, com busca), com os textos do contexto de
          pausa. `vinculadosAtuais` = quem já está salvo na janela: é o que mostra ao admin o que
          a edição vai mudar, e o que marca o selo "Na janela". */}
      <CollaboratorModal
        isOpen={isColaboradoresModalOpen}
        collaborators={colaboradoresPagina}
        selectedUsers={selecaoNoModal}
        setSelectedUsers={setSelecaoNoModal}
        vinculadosAtuais={
          editandoId
            ? (janelas
                .find((janela) => janela.id === editandoId)
                ?.colaboradores.map((colaborador) => ({
                  id: colaborador.id,
                  fullName: colaborador.nome,
                })) ?? [])
            : []
        }
        onConfirm={() => {
          setColaboradoresDaJanela(selecaoNoModal);
          setIsColaboradoresModalOpen(false);
        }}
        onCancel={() => setIsColaboradoresModalOpen(false)}
        totalCollaborators={totalColaboradores}
        loadPage={carregarPaginaDeColaboradores}
        isSidebarOpen={false}
        ocultarLimparSelecao
        textos={{
          titulo: 'Colaboradores da janela de pausa',
          atuaisPrefixo: 'Atualmente na janela',
          atuaisVazio: 'Nenhum colaborador nesta janela ainda',
          badgeAtual: 'Na janela',
          avisoRemocao: (quantidade) =>
            quantidade === 1
              ? '1 colaborador sairá desta janela ao salvar e deixará de ter esse tempo descontado nas próximas atividades finalizadas.'
              : `${quantidade} colaboradores sairão desta janela ao salvar e deixarão de ter esse tempo descontado nas próximas atividades finalizadas.`,
        }}
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Pausas Automáticas"
        secoes={helpSections}
      />

      <ConfirmModal
        isOpen={!!paraExcluir}
        title="Excluir janela de pausa"
        confirmLabel="Excluir"
        message={
          paraExcluir
            ? `Excluir a janela "${paraExcluir.nome}" (${paraExcluir.horaInicio} às ${paraExcluir.horaFim})? ` +
              `Ela alcança hoje: ${rotuloEscopo(paraExcluir)}. ` +
              'As atividades finalizadas daqui em diante deixam de ter esse desconto. ' +
              'Os descontos já aplicados em atividades finalizadas continuam no histórico e as horas ' +
              'já aprovadas não mudam.'
            : ''
        }
        onConfirm={() => void handleExcluir()}
        onCancel={() => setParaExcluir(null)}
      />

      <ConfirmModal
        isOpen={!!tipoParaExcluir}
        title="Excluir tipo de pausa"
        confirmLabel="Excluir"
        message={
          tipoParaExcluir
            ? `Excluir o tipo "${tipoParaExcluir.nome}"? ` +
              `As ${tipoParaExcluir.totalJanelas ?? 0} janela(s) desse tipo NÃO são excluídas: continuam ` +
              'ativas e descontando, apenas ficam sem classificação. Sem tipo, elas deixam de entrar na ' +
              'verificação que impede o mesmo colaborador em duas janelas do mesmo tipo.'
            : ''
        }
        onConfirm={() => void handleExcluirTipo()}
        onCancel={() => setTipoParaExcluir(null)}
      />
    </>
  );
};

/** Nome do dia como aparece na tabela do expediente. */
function nomeDoDia(diaSemana: number): string {
  return DIAS.find((dia) => dia.valor === diaSemana)?.nome ?? `Dia ${diaSemana}`;
}

/**
 * Monta as 7 linhas da tabela do expediente a partir do que veio do servidor.
 *
 * O flag `configurado` é só da tela: o backend representa "não restringe" pela AUSÊNCIA do dia, e a
 * tabela precisa mostrar as sete linhas mesmo assim. Ao salvar, os dias sem o flag são omitidos —
 * é o que os remove no PUT declarativo.
 */
function montarRascunho(doServidor: DiaExpediente[]): LinhaExpediente[] {
  return DIAS.map((dia) => {
    const existente = doServidor.find((registro) => registro.diaSemana === dia.valor);

    return existente
      ? {
          diaSemana: dia.valor,
          aberto: existente.aberto,
          aberturaMinutos: existente.aberturaMinutos,
          fechamentoMinutos: existente.fechamentoMinutos,
          configurado: true,
        }
      : {
          diaSemana: dia.valor,
          aberto: true,
          aberturaMinutos: null,
          fechamentoMinutos: null,
          configurado: false,
        };
  });
}

export default PausasAutomaticasSection;
